import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
// Components
import Tooltip from 'components/core/Tooltip';
import WithSkeleton from 'components/skeleton/WithSkeleton';
import _get from 'lodash/get';
import useCopyToClipboard from 'hooks/useClipboard';
import ParkingType from '../ParkingType/ParkingType';
import Status from '../Status/Status';
import DateParking from '../DateParking/DateParking';
import WarningOccupancy from '../WarningOccupancy/WarningOccupancy';
import Payment from '../Payment/Payment';
import { ParkingsContext } from '../../contexts/ParkingsContext';
import { createRedirectionLink } from '../../utils/utils';
// Icons
import { ReactComponent as RedirectionIcon } from '../../icons/redirection.svg';
import styles from './style.cssmodule.css';

function ParkingRow(parking) {
  const { formatMessage: t } = useIntl();
  const { parkingsLoading } = useContext(ParkingsContext);
  const {
    copyToClipboard, copied, justCopied, resetJustCopied,
  } = useCopyToClipboard();

  function copyToClipboardAndStopClickPropagation(e, value) {
    e.stopPropagation();
    copyToClipboard(value);
  }

  const showSkeleton = _get(parking, 'showSkeleton', false) || parkingsLoading;

  const firstname = _get(parking, 'customer.firstname', '');
  const lastname = _get(parking, 'customer.lastname', '');
  const fullname = firstname + (firstname ? ' ' : '') + lastname;
  const fullnameJustCopied = justCopied && copied && copied === fullname;

  const email = _get(parking, 'customer.email', '-');
  const emailJustCopied = justCopied && copied && copied === email;

  const phone = _get(parking, 'customer.phone', '-');
  const phoneJustCopied = justCopied && copied && copied === phone;

  const parkingType = _get(parking, 'functionalType', null);

  const carparkId = _get(parking, 'carpark.id', null);
  const carparkName = _get(parking, 'carpark.name', '').replace(/^Parking\s/, '');

  const carparkTimezone = _get(parking, 'carpark.timezone', null);
  const begin = _get(parking, 'begin', null);

  const reference = _get(parking, 'reference', null);
  const referenceJustCopied = justCopied && copied && copied === reference;

  const vehicles = _get(parking, 'vehicles', []);
  const licensePlates = vehicles.map((v) => _get(v, 'licensePlate', null)).filter((lp) => lp);
  const licensePlatesInString = licensePlates.join(', ');
  const firstLicensePlate = licensePlates.length > 0 ? licensePlates[0] : null;
  const licensePlateJustCopied = justCopied && copied && copied === licensePlatesInString;

  const status = _get(parking, 'status', null);

  const hasPaymentStatus = parkingType === 'BOOKING' || parkingType === 'ON_DEMAND';

  const redirectionLink = createRedirectionLink({
    parkingType,
    carparkId,
    reference,
    licensePlate: firstLicensePlate,
  });

  const redirectToParking = () => {
    window.open(redirectionLink, '_blank');
  };

  const redirectToCarparkHomepage = (e) => {
    e.stopPropagation();
    window.open(`#/carparks/${carparkId}`, '_blank');
  };

  const actions = [
    {
      label: 'redirection',
      icon: (
        <Tooltip content={t({ id: 'parking.table.actions.go' })}>
          <RedirectionIcon />
        </Tooltip>
      ),
    },
  ];

  const Actions = actions.map(({ label, icon }) => (
    <a key={label} href={redirectionLink} rel="noreferrer" target="_blank">
      {icon}
    </a>
  ));

  const Name = (
    <React.Fragment>
      <span>{firstname}</span>
      {firstname ? ' ' : ''}
      <b>{lastname}</b>
    </React.Fragment>
  );

  return (
    <tr key={parking.id} onClick={redirectToParking}>
      {/* 👥 Username */}
      <td className={styles.username} data-type="username">
        {WithSkeleton(
          <Tooltip
            content={
              firstname || lastname ? (
                <div className={styles.tooltipWithCopy}>
                  <span>{Name}</span>
                  <small>{t({ id: fullnameJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.usernameContainer}
              onClick={(e) => (firstname || lastname ? copyToClipboardAndStopClickPropagation(e, fullname) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {Name}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 140,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 📨 Email */}
      <td className={styles.email} data-type="email">
        {WithSkeleton(
          <Tooltip
            content={
              email ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{email}</div>
                  <small>{t({ id: emailJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.emailContainer}
              onClick={(e) => (email ? copyToClipboardAndStopClickPropagation(e, email) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {email || '-'}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 150,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🤳 Phone */}
      <td className={styles.phone} data-type="phoneNumber">
        {WithSkeleton(
          <Tooltip
            content={
              phone ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{phone}</div>
                  <small>{t({ id: phoneJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.phoneContainer}
              onClick={(e) => (phone ? copyToClipboardAndStopClickPropagation(e, phone) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              <span>{phone || '-'}</span>
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 120,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🅿️ Parking type */}
      <td className={styles.parkingType} data-type="parkingType">
        {WithSkeleton(<ParkingType type={parkingType} />)({
          loading: showSkeleton,
          width: 120,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🚘🚘🚘 Carpark */}
      <td className={styles.carpark} data-type="carpark">
        {WithSkeleton(
          <Tooltip
            content={
              carparkName ? (
                <div className={styles.tooltipWithCopy}>
                  <div style={{ textAlign: 'center' }}>
                    {carparkName}
                  </div>
                  <small>{t({ id: 'tooltip.clickToGoToCarpark' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.carparkContainer}
              onClick={(e) => (carparkName ? redirectToCarparkHomepage(e) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {carparkName}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 160,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🕒 Begin */}
      <td className={styles.begin} data-type="beginFrom">
        {WithSkeleton(<DateParking dateString={begin} timezone={carparkTimezone} />)({
          loading: showSkeleton,
          width: 100,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🔤 License plate */}
      <td className={styles.licensePlate} data-type="licensePlate">
        {WithSkeleton(
          <Tooltip
            content={
              licensePlates.length > 0 ? (
                <div className={styles.tooltipWithCopy}>
                  {licensePlates.map((licensePlate) => <div key={licensePlate}>{licensePlate}</div>)}
                  <small>{t({ id: licensePlateJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <span
              onClick={(e) => (firstLicensePlate ? copyToClipboardAndStopClickPropagation(e, licensePlatesInString) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {licensePlates.length > 1 ? `${firstLicensePlate} (+${licensePlates.length - 1})` : firstLicensePlate}
            </span>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 70,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🔠 Reference */}
      <td className={styles.reference} data-type="reference">
        {WithSkeleton(
          <Tooltip
            content={
              reference ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{reference}</div>
                  <small>{t({ id: referenceJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <span
              onClick={(e) => (reference ? copyToClipboardAndStopClickPropagation(e, reference) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {reference}
            </span>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 70,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🟠 Status */}
      <td className={styles.status} data-type="status">
        {WithSkeleton(
          <div className={styles.statusContainer}>
            <Tooltip content={<Status status={status} />}>
              <Status status={status} />
            </Tooltip>
            <WarningOccupancy parking={parking} />
          </div>,
        )({
          loading: showSkeleton,
          width: 90,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 💵 Payment */}
      <td className={styles.payment} data-type="payment">
        {WithSkeleton(hasPaymentStatus ? <Payment parking={parking} /> : null)({
          loading: showSkeleton,
          width: 70,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 💥 Action */}
      <td className={styles.actions} data-type="actions">
        {WithSkeleton(Actions)({
          loading: showSkeleton,
          width: 100,
          height: 20,
          debounceValue: 250,
        })}
      </td>
    </tr>
  );
}

export default ParkingRow;



// WEBPACK FOOTER //
// ./routes/cpm/routes/parkings/components/ParkingRow/ParkingRow.jsx